import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  InboxFilterBar,
  type InboxFilters,
} from "@/components/inbox/inbox-filter-bar";
import { InboxList } from "@/components/inbox/inbox-list";
import { InboxNotificationsMenu } from "@/components/inbox/inbox-notifications-menu";
import { presetToSince } from "@/components/inbox/date-range-filter";
import { TopBarPortal } from "@/components/layout/top-bar-portal";
import { useInbox } from "@/hooks/useInbox";
import {
  useInboxBatchExtraction,
  useInboxExtraction,
} from "@/hooks/useInboxExtraction";
import { useInboxNotifications } from "@/hooks/useInboxNotifications";
import { useSources } from "@/hooks/useSources";

const PAGE_SIZE = 200;

const DEFAULT_FILTERS: InboxFilters = {
  preset: "today",
  timeBasis: "captured",
  sourceIds: [],
  itemTypes: ["trade_alert", "buy", "sell"],
  symbol: undefined,
  showHidden: false,
};

/** Backend accepts one status at a time; hiding archived/ignore is done
 *  client-side by filtering the returned items. The total count from the
 *  server still reflects the unfiltered window — we recompute visible
 *  count below. */
const HIDDEN_STATUSES = new Set(["archived", "ignore"]);

export default function InboxPage() {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<InboxFilters>(DEFAULT_FILTERS);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedInboxItemIds, setSelectedInboxItemIds] = useState<Set<string>>(
    () => new Set(),
  );
  const openInboxItem = useCallback((inboxItemId: string) => {
    setFilters((prev) => ({
      ...prev,
      preset: "all",
      sourceIds: [],
      itemTypes: [],
      symbol: undefined,
      showHidden: true,
    }));
    setExpandedId(inboxItemId);
    window.setTimeout(() => {
      document
        .getElementById(`inbox-item-${inboxItemId}`)
        ?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 100);
  }, []);

  useEffect(() => {
    const inboxItemId = searchParams.get("item");
    if (inboxItemId) {
      openInboxItem(inboxItemId);
    }
  }, [openInboxItem, searchParams]);

  const sourcesQ = useSources();
  const since = useMemo(() => presetToSince(filters.preset), [filters.preset]);
  const inboxQ = useInbox({
    source_ids: filters.sourceIds,
    item_types: filters.itemTypes,
    symbol: filters.symbol,
    since,
    time_basis: filters.timeBasis,
    limit: PAGE_SIZE,
  });

  const sources = sourcesQ.data ?? [];
  const rawInboxItems = inboxQ.data?.items ?? [];
  const inboxNotifications = useInboxNotifications(rawInboxItems, openInboxItem);
  const extraction = useInboxExtraction();
  const batchExtraction = useInboxBatchExtraction();
  const visibleInboxItems = filters.showHidden
    ? rawInboxItems
    : rawInboxItems.filter((item) => !HIDDEN_STATUSES.has(item.status));
  const hiddenCount = rawInboxItems.length - visibleInboxItems.length;
  const selectedIds = useMemo(
    () => {
      const ids = visibleInboxItems
        .filter((item) => selectedInboxItemIds.has(item.id))
        .map((item) => item.id);
      return Array.from(new Set(ids));
    },
    [selectedInboxItemIds, visibleInboxItems],
  );
  const matchingUnstructuredCount = visibleInboxItems.filter(
    (item) => item.structured_payload === null,
  ).length;
  const batchFilters = useMemo(
    () => ({
      source_id: filters.sourceIds.length > 0 ? filters.sourceIds : undefined,
      item_type: filters.itemTypes.length > 0 ? filters.itemTypes : undefined,
      symbol: filters.symbol,
      since,
      time_basis: filters.timeBasis,
      limit: PAGE_SIZE,
    }),
    [filters.itemTypes, filters.sourceIds, filters.symbol, filters.timeBasis, since],
  );
  const analyzeInboxItems = useCallback(() => {
    if (selectedIds.length > 0) {
      extraction.mutate(
        { inbox_item_ids: selectedIds },
        { onSuccess: () => setSelectedInboxItemIds(new Set()) },
      );
      return;
    }

    batchExtraction.mutate({
      ...batchFilters,
      only_unstructured: true,
      force: false,
      group_size: 1,
    });
  }, [batchExtraction, batchFilters, extraction, selectedIds]);
  const analyzeInboxItem = useCallback(
    (inboxItemId: string) => {
      extraction.mutate({ inbox_item_ids: [inboxItemId] });
    },
    [extraction],
  );
  const isAnalyzing = extraction.isPending || batchExtraction.isPending;
  const analyzeTitle =
    selectedIds.length > 0
      ? `Analyze ${selectedIds.length} selected inbox item${selectedIds.length === 1 ? "" : "s"}`
      : `Analyze ${matchingUnstructuredCount} matching unstructured inbox item${matchingUnstructuredCount === 1 ? "" : "s"}`;
  const analyzeCount = selectedIds.length > 0 ? selectedIds.length : matchingUnstructuredCount;

  return (
    <div className="flex h-full flex-col">
      <TopBarPortal>
        <InboxNotificationsMenu
          history={inboxNotifications.history}
          permission={inboxNotifications.permission}
          freshCount={inboxNotifications.freshInboxItemIds.size}
          onRequestPermission={() => {
            void inboxNotifications.requestPermission();
          }}
          onDeleteNotification={inboxNotifications.deleteHistoryItem}
          onOpenInboxItem={(inboxItemId) => {
            inboxNotifications.clearFreshInboxItem(inboxItemId);
            openInboxItem(inboxItemId);
          }}
        />
      </TopBarPortal>
      <div className="flex flex-col gap-3 border-b border-border px-4 py-3 sm:gap-4 sm:px-6 sm:py-4">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold sm:text-xl">Signal Inbox</h1>
            <p className="hidden text-xs text-muted-fg sm:block">
              Raw vendor messages for triage.
            </p>
          </div>
          <div className="shrink-0 text-right text-xs text-muted-fg tabular-nums">
            {inboxQ.isLoading ? (
              "Loading…"
            ) : (
              <>
                {visibleInboxItems.length.toLocaleString()} shown
                {hiddenCount > 0 && !filters.showHidden ? (
                  <span className="ml-1 text-muted-fg/70">
                    · {hiddenCount} hidden
                  </span>
                ) : null}
              </>
            )}
          </div>
        </div>
        <InboxFilterBar
          sources={sources}
          filters={filters}
          onChange={(next) => {
            setFilters(next);
            setExpandedId(null);
            setSelectedInboxItemIds(new Set());
          }}
          onAnalyze={analyzeInboxItems}
          onRefresh={() => inboxQ.refetch()}
          analyzeTitle={analyzeTitle}
          analyzeCount={analyzeCount}
          isAnalyzing={isAnalyzing}
          isRefreshing={inboxQ.isFetching}
        />
      </div>

      <div className="flex-1 overflow-auto">
        <InboxList
          items={visibleInboxItems}
          sources={sources}
          expandedId={expandedId ?? undefined}
          selectedInboxItemIds={selectedInboxItemIds}
          onToggle={(item) =>
            setExpandedId((prev) => {
              inboxNotifications.clearFreshInboxItem(item.id);
              return prev === item.id ? null : item.id;
            })
          }
          onSelectedChange={(item, selected) =>
            setSelectedInboxItemIds((prev) => {
              const next = new Set(prev);
              if (selected) {
                next.add(item.id);
              } else {
                next.delete(item.id);
              }
              return next;
            })
          }
          onAnalyzeItem={(item) => analyzeInboxItem(item.id)}
          freshInboxItemIds={inboxNotifications.freshInboxItemIds}
          timeBasis={filters.timeBasis}
          isLoading={inboxQ.isLoading}
          isError={inboxQ.isError}
          errorMessage={(inboxQ.error as Error | undefined)?.message}
        />
      </div>
    </div>
  );
}
